import { Component } from '@theme/component';

/**
 * @typedef {Object} BundleContentsRefs
 * @property {HTMLElement} thumbnails - Thumbnails container
 * @property {HTMLButtonElement[]} tabs - Tab buttons
 * @property {HTMLElement[]} tabPanels - Tab panel elements
 */

/**
 * Wine bundle / wijnpakket: tab thumbnails + detail panels.
 * Loaded as `type="module"` from `snippets/scripts.liquid` — do not use `{% javascript %}` with `import` (runs as a classic script and throws).
 *
 * @extends {Component<BundleContentsRefs>}
 */
class BundleContents extends Component {
  /** @type {number} */
  #activeIndex = 0;

  /** @returns {HTMLButtonElement[]} */
  #resolveTabs() {
    const r = this.refs.tabs;
    if (Array.isArray(r) && r.length > 0) return /** @type {HTMLButtonElement[]} */ (r);
    return Array.from(this.querySelectorAll('.bundle-contents__thumb'));
  }

  /** @returns {HTMLElement[]} */
  #resolvePanels() {
    const r = this.refs.tabPanels;
    if (Array.isArray(r) && r.length > 0) return r;
    return Array.from(this.querySelectorAll('.bundle-contents__details [role="tabpanel"]'));
  }

  /**
   * Tab click: resolve the tab button via `closest` so clicks on the image / tooltip text still work.
   * @param {Event} event
   */
  handleTabClick(event) {
    event.preventDefault();
    const rawTarget = event.target;
    if (rawTarget == null) return;

    const el =
      rawTarget instanceof Element
        ? rawTarget
        : rawTarget.parentNode instanceof Element
          ? rawTarget.parentNode
          : null;
    const tab = el?.closest?.('button[data-bundle-index]');
    if (!(tab instanceof HTMLButtonElement)) return;

    const index = Number.parseInt(tab.dataset.bundleIndex ?? '', 10);
    if (Number.isNaN(index)) return;

    this.#activateTab(index);
  }

  /** @param {KeyboardEvent} event */
  handleTabKeydown(event) {
    const tabs = this.#resolveTabs();
    if (!tabs.length) return;

    const currentEl = event.target instanceof Element ? event.target : null;
    const currentTab = currentEl?.closest?.('button[data-bundle-index]');
    const currentIndex =
      currentTab instanceof HTMLButtonElement ? tabs.indexOf(currentTab) : -1;
    if (currentIndex === -1) return;

    let nextIndex = currentIndex;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      event.preventDefault();
      nextIndex = 0;
    } else if (event.key === 'End') {
      event.preventDefault();
      nextIndex = tabs.length - 1;
    } else {
      return;
    }

    this.#activateTab(nextIndex);
    tabs[nextIndex].focus();
  }

  /** @param {number} index */
  #activateTab(index) {
    const tabs = this.#resolveTabs();
    const panels = this.#resolvePanels();
    if (!tabs.length || !panels.length) return;
    if (index < 0 || index >= tabs.length || index >= panels.length) return;
    if (index === this.#activeIndex) return;

    for (const [i, tab] of tabs.entries()) {
      const isActive = i === index;

      tab.classList.toggle('bundle-contents__thumb--active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [i, panel] of panels.entries()) {
      const isActive = i === index;

      panel.classList.toggle('bundle-contents__panel--active', isActive);
      panel.hidden = !isActive;
    }

    this.#activeIndex = index;
  }
}

if (!customElements.get('bundle-contents')) {
  customElements.define('bundle-contents', BundleContents);
}
